import { useState } from 'react';
import {
  HORAS_PARA_CANCELAR_SIN_CARGO, type MetodoPago, type Modalidad, type PerfilProfesional, type ServicioPublico,
} from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { paciente } from '../../api/paciente';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import {
  AreaTexto, AvisoError, Boton, Cargando, Casilla, FalloDeCarga, OpcionTarjeta, Pasos, SelectorHorarios, Tarjeta,
} from '../../ui';
import { fechaLarga, pesos } from '../../ui/format';
import { Calendario } from './Calendario';
import { guardarReserva } from './reservaPendiente';

const METODOS: { metodo: MetodoPago; titulo: string; detalle: string }[] = [
  { metodo: 'tarjeta_credito', titulo: 'Tarjeta de crédito o débito', detalle: 'Visa, Mastercard, American Express' },
  { metodo: 'dinero_en_cuenta', titulo: 'MercadoPago', detalle: 'Con el dinero en tu cuenta' },
];

export function Reservar({ params, consulta }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => publico.perfil(id), [id]);
  if (pedido.estado === 'cargando') return <Cargando texto="Preparando la reserva" />;
  if (!pedido.datos) return <FalloDeCarga mensaje={pedido.error ?? 'No encontramos ese profesional'} alReintentar={pedido.recargar} />;
  return (
    <FormularioDeReserva
      profesional={pedido.datos}
      servicioInicial={Number(consulta.get('servicio')) || pedido.datos.servicios[0]?.id_servicio}
      pagoRechazado={consulta.get('pago') === 'rechazado'}
    />
  );
}

interface PropsFormulario {
  profesional: PerfilProfesional;
  servicioInicial: number | undefined;
  pagoRechazado: boolean;
}

function FormularioDeReserva({ profesional: p, servicioInicial, pagoRechazado }: PropsFormulario) {
  const cobraSena = p.cobra_sena && p.monto_sena !== null;
  const pasos = cobraSena ? ['Servicio', 'Fecha y hora', 'Pago de seña'] : ['Servicio', 'Fecha y hora', 'Confirmar'];
  const [paso, setPaso] = useState(0);
  const [servicio, setServicio] = useState<ServicioPublico | undefined>(
    p.servicios.find((s) => s.id_servicio === servicioInicial) ?? p.servicios[0]);
  const [modalidad, setModalidad] = useState<Modalidad>('presencial');
  const [fecha, setFecha] = useState<string | null>(null);
  const [hora, setHora] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [metodo, setMetodo] = useState<MetodoPago>('tarjeta_credito');
  const [acepta, setAcepta] = useState(false);
  const [error, setError] = useState<string | null>(pagoRechazado ? 'El pago fue rechazado y no se reservó el turno. Probá con otro medio de pago.' : null);
  const [enviando, setEnviando] = useState(false);

  const horarios = usePedido(
    () => (fecha && servicio ? publico.horarios(p.id_profesional, fecha, servicio.id_servicio) : Promise.resolve([])),
    [fecha, servicio?.id_servicio],
  );
  const nombre = `${p.nombre} ${p.apellido}`;
  if (!servicio) return <FalloDeCarga mensaje="Este profesional no tiene servicios disponibles" />;

  const reserva = {
    profesional: p.id_profesional, servicio: servicio.id_servicio, fecha: fecha ?? '', hora: hora ?? '',
    modalidad, motivo: motivo.trim() || undefined, metodo,
  };

  const confirmarSinSena = async () => {
    setEnviando(true);
    try {
      const turno = await paciente.reservar(reserva);
      irA(`/confirmacion/${turno.id_turno}`, { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos reservar el turno. Probá de nuevo.');
      setEnviando(false);
    }
  };

  const pagar = () => {
    guardarReserva({ reserva, profesional: nombre, servicio: servicio.nombre, sena: p.monto_sena ?? 0 });
    irA('/pago');
  };

  return (
    <div className="reserva">
      <h1 className="reserva__titulo">Reservá con {nombre}</h1>
      <p className="reserva__bajada">{p.especialidad} · {p.direccion}, {p.barrio}</p>
      <Pasos pasos={pasos} actual={paso} />
      <AvisoError>{error}</AvisoError>

      {paso === 0 && (
        <Tarjeta>
          <fieldset className="reserva__grupo">
            <legend>Elegí el servicio</legend>
            {p.servicios.map((s) => (
              <OpcionTarjeta key={s.id_servicio} name="servicio" checked={s.id_servicio === servicio.id_servicio}
                onChange={() => { setServicio(s); setHora(null); }}
                titulo={s.nombre} detalle={`${s.duracion_min} minutos`} extra={<strong>{pesos(s.precio)}</strong>} />
            ))}
          </fieldset>
          {servicio.permite_videoconsulta && (
            <fieldset className="reserva__grupo">
              <legend>¿Cómo querés atenderte?</legend>
              <OpcionTarjeta name="modalidad" checked={modalidad === 'presencial'} onChange={() => setModalidad('presencial')}
                titulo="En el consultorio" detalle={`${p.direccion}, ${p.barrio}`} />
              <OpcionTarjeta name="modalidad" checked={modalidad === 'videoconsulta'} onChange={() => setModalidad('videoconsulta')}
                titulo="Por videoconsulta" detalle="Te mandamos el enlace cuando se confirma el turno" />
            </fieldset>
          )}
          <div className="reserva__acciones"><Boton onClick={() => setPaso(1)}>Continuar</Boton></div>
        </Tarjeta>
      )}

      {paso === 1 && (
        <Tarjeta>
          <div className="reserva__agenda">
            <Calendario elegida={fecha} alElegir={(f) => { setFecha(f); setHora(null); }} />
            <div>
              {!fecha && <p className="reserva__nota">Elegí un día para ver los horarios libres.</p>}
              {fecha && horarios.estado === 'cargando' && <Cargando texto="Buscando horarios" />}
              {fecha && horarios.estado === 'listo' && horarios.datos?.length === 0 && (
                <p className="reserva__nota">No quedan horarios libres el {fechaLarga(fecha)}. Probá con otra fecha.</p>
              )}
              {fecha && horarios.datos && horarios.datos.length > 0 && (
                <SelectorHorarios horarios={horarios.datos} elegido={hora} alElegir={setHora} />
              )}
            </div>
          </div>
          <AreaTexto etiqueta="Motivo de la consulta" placeholder="Opcional: contale al profesional qué te pasa"
            maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          <div className="reserva__acciones">
            <Boton variante="secundario" onClick={() => setPaso(0)}>Volver</Boton>
            <Boton disabled={!fecha || !hora} onClick={() => setPaso(2)}>Continuar</Boton>
          </div>
        </Tarjeta>
      )}

      {paso === 2 && fecha && hora && (
        <Tarjeta>
          <dl className="reserva__resumen">
            <div><dt>Servicio</dt><dd>{servicio.nombre}</dd></div>
            <div><dt>Cuándo</dt><dd>{fechaLarga(fecha)} a las {hora}</dd></div>
            <div><dt>Precio de la consulta</dt><dd>{pesos(servicio.precio)}</dd></div>
            {cobraSena && <div><dt>Seña a pagar ahora</dt><dd>{pesos(p.monto_sena ?? 0)}</dd></div>}
          </dl>
          {cobraSena ? (
            <>
              <fieldset className="reserva__grupo">
                <legend>Medio de pago</legend>
                {METODOS.map((m) => (
                  <OpcionTarjeta key={m.metodo} name="metodo" checked={metodo === m.metodo}
                    onChange={() => setMetodo(m.metodo)} titulo={m.titulo} detalle={m.detalle} />
                ))}
              </fieldset>
              <p className="reserva__politica">
                Si cancelás con al menos {HORAS_PARA_CANCELAR_SIN_CARGO} horas de anticipación te devolvemos la seña.
                Si cancelás después o no vas, la seña queda para el profesional.
              </p>
              <Casilla checked={acepta} onChange={(e) => setAcepta(e.target.checked)}
                etiqueta="Entiendo y acepto la política de cancelación" />
            </>
          ) : (
            <p className="reserva__politica">Este profesional no cobra seña: el turno se confirma al instante.</p>
          )}
          <div className="reserva__acciones">
            <Boton variante="secundario" onClick={() => setPaso(1)}>Volver</Boton>
            {cobraSena
              ? <Boton disabled={!acepta} onClick={pagar}>Pagar {pesos(p.monto_sena ?? 0)} y confirmar turno</Boton>
              : <Boton cargando={enviando} onClick={() => void confirmarSinSena()}>Confirmar turno</Boton>}
          </div>
        </Tarjeta>
      )}
    </div>
  );
}
